import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import Navbar from '../components/Navbar';
import {
  UsersIcon,
  BeakerIcon,
  ChartBarIcon,
  CogIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function AdminHomePage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, [user]);

  const checkAuthorization = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    // TEMPORARY: Hardcode john@flowlogicllc.com to bypass RLS check
    if (user.email === 'john@flowlogicllc.com') {
      console.log('Admin access granted for john@flowlogicllc.com (temporary bypass)');
      setIsAuthorized(true);
      setLoading(false);
      return;
    }

    // Check system_admins table
    try {
      const { data: systemAdmin, error } = await supabase
        .from('system_admins')
        .select('email')
        .eq('email', user.email)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking admin status:', error);
        alert('Error checking admin permissions. Please contact support.');
        navigate('/dashboard');
        return;
      }

      if (systemAdmin) {
        setIsAuthorized(true);
      } else {
        console.log('User not authorized for admin:', user.email);
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Failed to check admin authorization:', error);
      alert('Failed to verify admin permissions. Please contact support.');
      navigate('/dashboard');
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar onSignOut={signOut} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Navbar onSignOut={signOut} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-red-50 p-8 rounded-lg text-center">
            <h2 className="text-2xl font-bold text-red-800 mb-4">Access Denied</h2>
            <p className="text-red-600">You don't have permission to access the admin area.</p>
          </div>
        </div>
      </div>
    );
  }

  const adminSections = [
    {
      title: 'Account Admin',
      description: 'Manage accounts, access codes, and subscriptions',
      icon: UsersIcon,
      href: '/admin/accounts',
      color: 'blue'
    },
    {
      title: 'FlowDocs Labs',
      description: 'Experimental features and advanced tooling',
      icon: BeakerIcon,
      href: '/admin/labs',
      color: 'purple'
    },
    {
      title: 'Industry Insights',
      description: 'Analytics and insights across the platform',
      icon: ChartBarIcon,
      href: '/admin/industry-insights',
      color: 'green'
    },
    {
      title: 'Reactivation Analytics',
      description: 'Monitor and optimize account reactivation system',
      icon: ArrowPathIcon,
      href: '/admin/reactivation',
      color: 'orange'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={signOut} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <CogIcon className="h-10 w-10 text-gray-700" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                System Admin
              </h1>
              <p className="text-gray-600 mt-1">FlowDocs administrative tools and settings</p>
            </div>
          </div>
        </div>

        {/* Admin Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {adminSections.map((section) => {
            const Icon = section.icon;
            const colorClasses = {
              blue: 'bg-blue-50 text-blue-600 group-hover:bg-blue-100',
              purple: 'bg-purple-50 text-purple-600 group-hover:bg-purple-100',
              green: 'bg-green-50 text-green-600 group-hover:bg-green-100',
              orange: 'bg-orange-50 text-orange-600 group-hover:bg-orange-100'
            };

            return (
              <Link
                key={section.href}
                to={section.href}
                className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-6"
              >
                <div className={`inline-flex p-3 rounded-lg ${colorClasses[section.color as keyof typeof colorClasses]} transition-colors mb-4`}>
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {section.title}
                </h3>
                <p className="text-sm text-gray-600">
                  {section.description}
                </p>
              </Link>
            );
          })}
        </div>

        {/* Welcome Message */}
        <div className="mt-8 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Welcome to System Admin</h2>
          <p className="text-gray-600">
            This is your central hub for administrative tools. Select a section above to get started.
          </p>
        </div>
      </div>
    </div>
  );
}
